import { TESTDATA_MODAL } from '@enums/overlay-internal-events.enum';
import { TEST_DATA_SYMBOLS } from '@enums/test-data-symbols.enum';
import UploadService from '@services/uploads.service';
import {
  Icon,
  toastify,
  useAnimatedClass,
  useInternalEvents,
  useTranslation,
  NOOP,
} from '@testsigma/atoms';
import { Upload, UploadSelector } from '@testsigma/organism';
import classNames from 'classnames';
import { useCallback, useState } from 'react';
import {
  TestDataDrawerHelpInfoProps,
  TestDataDrawerLayout,
} from './test-data-drawer-layout';
import { UploadFormModal } from '@modules/test-development/components/uploads/upload-modal';
import { useInfiniteQuery } from 'react-query';
import { useReactQueryCaching } from '@hooks/use-react-query-caching';
import { CachingPrefix } from '@enums/caching-prefix.enum';

const UploadNewFileButton = ({
  setUploadModal,
}: {
  setUploadModal: (value: boolean) => void;
}) => {
  const { t } = useTranslation();
  return (
    <div
      className='flex items-center justify-center cursor-pointer gap-x-2 w-full mb-6 p-4 rounded-lg border'
      onClick={() => setUploadModal(true)}
    >
      <Icon name='upload' className='text-primary-500 text-xl' />
      <span className='text-base text-primary-500'>{t('upload.new.file')}</span>
    </div>
  );
};

export const UploadDrawer = ({
  applicationId,
  isDetached = false,
  onClose = NOOP,
  onSelect = (item) => {},
  className = '',
}) => {
  const { t } = useTranslation();
  const publishInternalEvents = useInternalEvents([]);
  const handleClose = useCallback(() => {
    if (isDetached) onClose();
    else publishInternalEvents(TESTDATA_MODAL.CLOSE_UPLOAD);
  }, [publishInternalEvents]);
  const { getCachingTime } = useReactQueryCaching(
    CachingPrefix.FETCH_TESTDATA_UPLOADS,
    false,
  );
  const [showUploadModal, setUploadModal] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState('');
  const onDataClick = useCallback(
    (item) => {
      if (isDetached) onSelect(item);
      else publishInternalEvents(TESTDATA_MODAL.SELECTED_UPLOAD, item);
    },
    [publishInternalEvents],
  );
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    refetch: refetchList,
  } = useInfiniteQuery(
    [`${CachingPrefix.FETCH_TESTDATA_UPLOADS}-${searchTerm || ''}`],
    async ({ pageParam = 0 }) => {
      let query = `applicationId:${applicationId}${
        !!searchTerm ? `,name:*${searchTerm}*,` : ''
      }`;
      let checkData = await UploadService.findAll(query, null, 20, pageParam);
      return checkData;
    },

    {
      getNextPageParam: (lastPage) =>
        lastPage?.last ? undefined : lastPage?.pageable?.pageNumber + 1,
      staleTime: getCachingTime(),
      cacheTime: getCachingTime(),
    },
  );

  const modifiedData = data?.pages?.flatMap((item) => item?.content) || [];
  const helpInfoProps: TestDataDrawerHelpInfoProps = {
    description: t('modal.help.upload'),
    url: 'https://testsigma.com/docs/uploads/upload-files/',
  };

  const animationClass = useAnimatedClass('reval-animation-opacity');

  return (
    <>
      <TestDataDrawerLayout
        title={t('modal.title.uploads')}
        symbol={TEST_DATA_SYMBOLS.UPLOAD}
        onClose={handleClose}
        helpInfo={helpInfoProps}
        className={className}
      >
        <div
          className={classNames(
            'px-5 py-6 h-full overflow-hidden',
            animationClass,
          )}
        >
          <UploadSelector
            uploads={modifiedData}
            onClick={onDataClick}
            isFetching={isFetching}
            loadNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            onChangeSearch={setSearchTerm}
            searchQuery={searchTerm}
            uploadNewFileButton={
              <UploadNewFileButton setUploadModal={setUploadModal} />
            }
          />
        </div>
      </TestDataDrawerLayout>
      {showUploadModal && (
        <UploadFormModal
          upload={new Upload()}
          show={true}
          onClose={() => setUploadModal(false)}
          onSuccess={() => {
            refetchList();
            toastify(t('common.upload_completed.message'), {
              type: 'success',
              position: 'top-center',
            });
            setUploadModal(false);
          }}
        />
      )}
    </>
  );
};
